import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import EditProfileModal from '../components/EditProfileModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import UserUnavailabilityModal from '../components/UserUnavailabilityModal';
import ConfirmationModal from '../components/ConfirmationModal';
import CreateOrganizationModal from '../components/CreateOrganizationModal';
import { toast } from 'react-toastify';
import apiClient from '../api/axios';
import { Button, Card, Dropdown, Modal, Form } from 'react-bootstrap';

// Custom toggle for the actions dropdown
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="btn btn-link text-dark p-2 border-0"
    >
      {children}
    </button>
));

const ProfilePage = () => {
    const { user, organization, reloadUser, logout } = useAuth();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [unavailabilityPeriods, setUnavailabilityPeriods] = useState([]);
    const [showUnavailabilityModal, setShowUnavailabilityModal] = useState(false);
    const [editingUnavailability, setEditingUnavailability] = useState(null);
    const [showConfirmDeleteUnavailabilityModal, setShowConfirmDeleteUnavailabilityModal] = useState(false);
    const [unavailabilityToDeleteId, setUnavailabilityToDeleteId] = useState(null);
    const [showCreateOrganizationModal, setShowCreateOrganizationModal] = useState(false);
    const [showLeaveOrgModal, setShowLeaveOrgModal] = useState(false);
    const [showDeleteProfileModal, setShowDeleteProfileModal] = useState(false);
    const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');

    const fetchUnavailabilityPeriods = useCallback(async () => {
        if (user) {
            try {
                const response = await apiClient.get('/users/me/unavailability/');
                setUnavailabilityPeriods(response.data);
            } catch (error) {
                toast.error('Ошибка загрузки периодов недоступности.');
                console.error('Error fetching unavailability periods:', error);
            }
        }
    }, [user]);

    useEffect(() => {
        if (organization) {
            fetchUnavailabilityPeriods();
        }
    }, [fetchUnavailabilityPeriods, organization]);

    const handleLeaveOrganization = async () => {
        try {
            await apiClient.post('/users/me/leave-organization');
            toast.success('Вы успешно покинули организацию.');
            await reloadUser();
            setShowLeaveOrgModal(false);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Ошибка при выходе из организации.';
            toast.error(errorMessage);
        }
    };

    const handleDeleteProfile = async () => {
        if (deleteConfirmationInput !== user.email) {
            toast.error('Введенный email не совпадает с вашим.');
            return;
        }
        try {
            await apiClient.delete('/users/me');
            toast.success('Ваш профиль был успешно удален.');
            logout(); // Logout the user
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Ошибка при удалении профиля.';
            toast.error(errorMessage);
        }
    };

    const handleOpenAddUnavailabilityModal = () => {
        setEditingUnavailability(null);
        setShowUnavailabilityModal(true);
    };

    const handleOpenEditUnavailabilityModal = (period) => {
        setEditingUnavailability(period);
        setShowUnavailabilityModal(true);
    };

    const handleCloseUnavailabilityModal = () => {
        setShowUnavailabilityModal(false);
        setEditingUnavailability(null);
    };

    const handleSaveUnavailability = async (formData) => {
        try {
            if (editingUnavailability) {
                await apiClient.put(`/users/me/unavailability/${editingUnavailability.id}`, formData);
                toast.success('Период недоступности успешно обновлен!');
            } else {
                await apiClient.post('/users/me/unavailability/', formData);
                toast.success('Период недоступности успешно добавлен!');
            }
            handleCloseUnavailabilityModal();
            fetchUnavailabilityPeriods();
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Ошибка сохранения периода недоступности.';
            toast.error(errorMessage);
            console.error('Error saving unavailability period:', error);
        }
    };

    const handleDeleteUnavailabilityClick = (id) => {
        setUnavailabilityToDeleteId(id);
        setShowConfirmDeleteUnavailabilityModal(true);
    };

    const handleConfirmDeleteUnavailability = async () => {
        try {
            await apiClient.delete(`/users/me/unavailability/${unavailabilityToDeleteId}`);
            toast.success('Период недоступности успешно удален!');
            setShowConfirmDeleteUnavailabilityModal(false);
            setUnavailabilityToDeleteId(null);
            fetchUnavailabilityPeriods();
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Ошибка удаления периода недоступности.';
            toast.error(errorMessage);
            console.error('Error deleting unavailability period:', error);
        }
    };

    const handleCancelDeleteUnavailability = () => {
        setShowConfirmDeleteUnavailabilityModal(false);
        setUnavailabilityToDeleteId(null);
    };

    const handleCreateOrganizationSuccess = async () => {
        await reloadUser();
        setShowCreateOrganizationModal(false);
    };

    if (!user) {
        return <div className="text-center">Загрузка профиля...</div>;
    }

    return (
        <>
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <Card className="mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h2 className="h4 mb-0">Профиль пользователя</h2>
                            <Dropdown>
                                <Dropdown.Toggle as={CustomToggle} id="profile-actions-dropdown">
                                    <i className="fas fa-ellipsis-v"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                    <Dropdown.Item onClick={() => setShowEditModal(true)}>Редактировать имя</Dropdown.Item>
                                    <Dropdown.Item onClick={() => setShowChangePasswordModal(true)}>Сменить пароль</Dropdown.Item>
                                    <Dropdown.Divider />
                                    {organization && user.role_in_organization !== 'owner' && (
                                        <Dropdown.Item onClick={() => setShowLeaveOrgModal(true)} className="text-danger">
                                            Выйти из организации
                                        </Dropdown.Item>
                                    )}
                                    <Dropdown.Item onClick={() => setShowDeleteProfileModal(true)} className="text-danger">
                                        Удалить профиль
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Card.Header>
                        <Card.Body>
                            <p><strong>Email:</strong> {user.email}</p>
                            <p><strong>Имя:</strong> {user.name || 'Не указано'}</p>
                            <p><strong>Организация:</strong>
                                {organization ? (
                                    <span> {organization.name}</span>
                                ) : (
                                    <span className="text-muted">
                                        {' '}
                                        Вы не состоите ни в одной организации. Вы можете{' '}
                                        <a href="#" onClick={(e) => { e.preventDefault(); setShowCreateOrganizationModal(true); }}>
                                            создать свою
                                        </a>
                                        {' '}или присоединиться к существующей организации, обратившись к ее администратору.
                                    </span>
                                )}
                            </p>
                        </Card.Body>
                    </Card>

                    {organization && (
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h2 className="h4 mb-0">
                                    Периоды недоступности
                                    <small className="d-block text-muted" style={{ fontSize: '0.9rem' }}>Когда вас нельзя добавить в событие</small>
                                </h2>
                                <Button variant="primary" size="sm" onClick={handleOpenAddUnavailabilityModal} title="Добавить период недоступности">
                                    <i className="fas fa-plus"></i> Добавить период
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {unavailabilityPeriods.length > 0 ? (
                                    <ul className="list-group">
                                        {unavailabilityPeriods.map(period => (
                                            <li key={period.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                {new Date(period.start_date).toLocaleDateString()} - {new Date(period.end_date).toLocaleDateString()}
                                                <div className="dropdown">
                                                    <button className="btn btn-sm btn-light" type="button" id={`dropdownMenuButton-${period.id}`} data-bs-toggle="dropdown" aria-expanded="false">
                                                        <i className="fas fa-ellipsis-v"></i>
                                                    </button>
                                                    <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${period.id}`}>
                                                        <li>
                                                            <button className="dropdown-item" type="button" onClick={() => handleOpenEditUnavailabilityModal(period)}>
                                                                Редактировать
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button className="dropdown-item text-danger" type="button" onClick={() => handleDeleteUnavailabilityClick(period.id)}>
                                                                Удалить
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p>Периоды недоступности не добавлены.</p>
                                )}
                            </Card.Body>
                        </Card>
                    )}
                </div>
            </div>

            {/* Modals */}
            <EditProfileModal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                user={user}
            />
            <ChangePasswordModal 
                show={showChangePasswordModal} 
                onHide={() => setShowChangePasswordModal(false)} 
            />
            <UserUnavailabilityModal
                show={showUnavailabilityModal}
                handleClose={handleCloseUnavailabilityModal}
                onSave={handleSaveUnavailability}
                unavailability={editingUnavailability}
            />
            <ConfirmationModal
                show={showConfirmDeleteUnavailabilityModal}
                title="Подтвердить удаление периода недоступности"
                message="Вы уверены, что хотите удалить этот период недоступности?"
                onConfirm={handleConfirmDeleteUnavailability}
                onCancel={handleCancelDeleteUnavailability}
                confirmText="Удалить"
                cancelText="Отмена"
            />
            <CreateOrganizationModal
                show={showCreateOrganizationModal}
                onHide={() => setShowCreateOrganizationModal(false)}
                onSuccess={handleCreateOrganizationSuccess}
            />
            <ConfirmationModal
                show={showLeaveOrgModal}
                title="Подтверждение выхода из организации"
                message="Вы уверены, что хотите покинуть организацию? Это действие нельзя будет отменить."
                onConfirm={handleLeaveOrganization}
                onCancel={() => setShowLeaveOrgModal(false)}
                confirmText="Выйти"
                cancelText="Отмена"
                confirmVariant="danger"
            />
            <Modal show={showDeleteProfileModal} onHide={() => setShowDeleteProfileModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">Удалить профиль</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Это действие необратимо. Ваш профиль и все связанные с ним данные будут удалены.</p>
                    <p>Если вы являетесь владельцем организации, сначала передайте владение другому пользователю.</p>
                    <p>Для подтверждения введите ваш email: <strong>{user.email}</strong></p>
                    <Form.Group>
                        <Form.Control
                            type="email"
                            placeholder="Введите ваш email"
                            value={deleteConfirmationInput}
                            onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                            autoFocus
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteProfileModal(false)}>Отмена</Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDeleteProfile}
                        disabled={deleteConfirmationInput !== user.email}
                    >
                        Я понимаю, удалить мой профиль
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ProfilePage;
